import paper from 'paper';
var MAX_RECURSION_DEPTH = 12;
var AREA_EPSILON_FACTOR = Number.EPSILON * 1024;
var INTERIOR_CLEARANCE_FACTOR = 0.995;
var STRATEGIES = [
    {
        algorithm: 'adaptive',
        splitSelfIntersectingCurves: true,
        guardInsideJoins: true,
        simplifySelfIntersectingStroke: true,
        adaptiveFallback: true,
    },
    {
        algorithm: 'robust',
        splitSelfIntersectingCurves: true,
        guardInsideJoins: true,
        simplifySelfIntersectingStroke: true,
        adaptiveFallback: false,
    },
    {
        algorithm: 'split',
        splitSelfIntersectingCurves: true,
        guardInsideJoins: false,
        simplifySelfIntersectingStroke: false,
        adaptiveFallback: false,
    },
    {
        algorithm: 'legacy',
        splitSelfIntersectingCurves: false,
        guardInsideJoins: false,
        simplifySelfIntersectingStroke: false,
        adaptiveFallback: false,
    },
];
// Adaptive already evaluates the strict robust candidate, so running robust again in auto is redundant.
var AUTO_STRATEGIES = STRATEGIES.filter(function (strategy) { return strategy.algorithm !== 'robust'; });
function getStrategy(algorithm) {
    if (algorithm === 'auto') {
        return STRATEGIES[0];
    }
    return STRATEGIES.find(function (strategy) { return strategy.algorithm === algorithm; }) || STRATEGIES[0];
}
/**
 * Offset the start/terminal segment of a bezier curve
 * @param segment segment to offset
 * @param curve curve to offset
 * @param handleNormal the normal of the the line formed of two handles
 * @param offset offset value
 */
function offsetSegment(segment, curve, handleNormal, offset) {
    var isFirst = segment.curve === curve;
    // get offset vector
    var offsetVector = (curve.getNormalAtTime(isFirst ? 0 : 1)).multiply(offset);
    // get offset point
    var point = segment.point.add(offsetVector);
    var newSegment = new paper.Segment(point);
    // handleOut for start segment & handleIn for terminal segment
    var handle = (isFirst ? 'handleOut' : 'handleIn');
    newSegment[handle] = segment[handle].add(handleNormal.subtract(offsetVector).divide(2));
    return newSegment;
}
/**
 * Adaptive offset a curve by repeatly apply the approximation proposed by Tiller and Hanson.
 * @param curve curve to offset
 * @param offset offset value
 */
function adaptiveOffsetCurve(curve, offset, strategy, recursionTime) {
    if (recursionTime === void 0) { recursionTime = 0; }
    var hNormal = (new paper.Curve(curve.segment1.handleOut.add(curve.segment1.point), new paper.Point(0, 0), new paper.Point(0, 0), curve.segment2.handleIn.add(curve.segment2.point))).getNormalAtTime(0.5).multiply(offset);
    var segment1 = offsetSegment(curve.segment1, curve, hNormal, offset);
    var segment2 = offsetSegment(curve.segment2, curve, hNormal, offset);
    var threshold = Math.min(Math.abs(offset) / 10, 1);
    var minCurveLength = Math.max(threshold * 0.25, 0.0001);
    if (recursionTime >= MAX_RECURSION_DEPTH || curve.length <= minCurveLength) {
        return [segment1, segment2];
    }
    // divide && re-offset
    var offsetCurve = new paper.Curve(segment1, segment2);
    var offsetCurveIntersections = offsetCurve.getIntersections(offsetCurve);
    var midOffset = offsetCurve.getPointAtTime(0.5).getDistance(curve.getPointAtTime(0.5));
    var needsSplit = strategy.splitSelfIntersectingCurves
        ? offsetCurveIntersections.length > 0 || Math.abs(midOffset - Math.abs(offset)) > threshold
        : offsetCurveIntersections.length === 0 && Math.abs(midOffset - Math.abs(offset)) > threshold;
    if (needsSplit) {
        var subCurve = curve.divideAtTime(0.5);
        if (subCurve != null) {
            return adaptiveOffsetCurve(curve, offset, strategy, recursionTime + 1)
                .concat(adaptiveOffsetCurve(subCurve, offset, strategy, recursionTime + 1));
        }
    }
    return [segment1, segment2];
}
/**
 * Create a round join segment between two adjacent segments.
 */
function makeRoundJoin(segment1, segment2, originPoint, radius) {
    var through = segment1.point.subtract(originPoint).add(segment2.point.subtract(originPoint))
        .normalize(Math.abs(radius)).add(originPoint);
    var arc = new paper.Path.Arc({ from: segment1.point, to: segment2.point, through: through, insert: false });
    segment1.handleOut = arc.firstSegment.handleOut;
    segment2.handleIn = arc.lastSegment.handleIn;
    return arc.segments.length === 3 ? arc.segments[1] : null;
}
function det(p1, p2) {
    return p1.x * p2.y - p1.y * p2.x;
}
/**
 * Get the intersection point of point based lines
 */
function getPointLineIntersections(p1, p2, p3, p4) {
    var l1 = p1.subtract(p2);
    var l2 = p3.subtract(p4);
    var dl1 = det(p1, p2);
    var dl2 = det(p3, p4);
    return new paper.Point(dl1 * l2.x - l1.x * dl2, dl1 * l2.y - l1.y * dl2).divide(det(l1, l2));
}
/**
 * Connect two adjacent bezier curve, each curve is represented by two segments,
 * create different types of joins or simply removal redundant segment.
 */
function isOutsideJoin(origin, offset) {
    var _a;
    var previousCurve = (_a = origin.previous) === null || _a === void 0 ? void 0 : _a.curve;
    var nextCurve = origin.curve;
    if (!previousCurve || !nextCurve) {
        return true;
    }
    var tangentIn = previousCurve.getTangentAtTime(1);
    var tangentOut = nextCurve.getTangentAtTime(0);
    var turn = det(tangentIn, tangentOut);
    return Math.abs(turn) < 1e-9 || turn * offset > 0;
}
function connectAdjacentBezier(segments1, segments2, origin, joinType, offset, limit, closed, strategy) {
    var curve1 = new paper.Curve(segments1[0], segments1[1]);
    var curve2 = new paper.Curve(segments2[0], segments2[1]);
    var intersection = curve1.getIntersections(curve2);
    var distance = segments1[1].point.getDistance(segments2[0].point);
    var shouldJoin = !closed || !strategy.guardInsideJoins || isOutsideJoin(origin, offset);
    if (origin.isSmooth()) {
        segments2[0].handleOut = segments2[0].handleOut.project(origin.handleOut);
        segments2[0].handleIn = segments1[1].handleIn.project(origin.handleIn);
        segments2[0].point = segments1[1].point.add(segments2[0].point).divide(2);
        segments1.pop();
    }
    else {
        if (intersection.length === 0) {
            if (distance > Math.abs(offset) * 0.1) {
                // connect
                switch (joinType) {
                    case 'miter':
                        if (shouldJoin) {
                            var join = getPointLineIntersections(curve1.point2, curve1.point2.add(curve1.getTangentAtTime(1)), curve2.point1, curve2.point1.add(curve2.getTangentAtTime(0)));
                            // prevent sharp angle
                            var joinOffset = Math.max(join.getDistance(curve1.point2), join.getDistance(curve2.point1));
                            if (joinOffset < Math.abs(offset) * limit) {
                                segments1.push(new paper.Segment(join));
                            }
                        }
                        break;
                    case 'round':
                        if (shouldJoin) {
                            var mid = makeRoundJoin(segments1[1], segments2[0], origin.point, offset);
                            if (mid) {
                                segments1.push(mid);
                            }
                        }
                        break;
                    default: break;
                }
            }
            else {
                segments2[0].handleIn = segments1[1].handleIn;
                segments1.pop();
            }
        }
        else {
            var second1 = curve1.divideAt(intersection[0]);
            if (second1) {
                var join = second1.segment1;
                var second2 = curve2.divideAt(curve2.getIntersections(curve1)[0]);
                join.handleOut = second2 ? second2.segment1.handleOut : segments2[0].handleOut;
                segments1.pop();
                segments2[0] = join;
            }
            else {
                segments2[0].handleIn = segments1[1].handleIn;
                segments1.pop();
            }
        }
    }
}
/**
 * Connect all the segments together.
 */
function connectBeziers(rawSegments, join, source, offset, limit, strategy) {
    var originSegments = source.segments;
    var first = rawSegments[0].slice();
    for (var i = 0; i < rawSegments.length - 1; ++i) {
        connectAdjacentBezier(rawSegments[i], rawSegments[i + 1], originSegments[i + 1], join, offset, limit, source.closed, strategy);
    }
    if (source.closed) {
        connectAdjacentBezier(rawSegments[rawSegments.length - 1], first, originSegments[0], join, offset, limit, source.closed, strategy);
        rawSegments[0][0] = first[0];
    }
    return rawSegments;
}
function reduceSingleChildCompoundPath(path) {
    if (path.children.length === 1) {
        path = path.children[0];
        path.remove(); // remove from parent, this is critical, or the style attributes will be ignored
    }
    return path;
}
function getAreaEpsilon(path) {
    var scale = Math.max(path.bounds.width, path.bounds.height, 1);
    return Math.max(scale * scale * AREA_EPSILON_FACTOR, Number.EPSILON);
}
/** Normalize a path, always clockwise, non-self-intersection, remove numerical debris, and no one-component compound path. */
function normalize(path) {
    if (path instanceof paper.CompoundPath) {
        path.children
            .filter(function (child) { return !child.closed; })
            .forEach(function (child) { return child.remove(); });
        if (path.children.length === 1) {
            path = reduceSingleChildCompoundPath(path);
        }
    }
    if (path.closed) {
        var ignoreArea_1 = getAreaEpsilon(path);
        if (!path.clockwise) {
            path.reverse();
        }
        path = path.unite(path, { insert: false });
        path = path.resolveCrossings();
        if (path instanceof paper.CompoundPath) {
            path.children
                .filter(function (child) { return !child.closed || Math.abs(child.area) < ignoreArea_1; })
                .forEach(function (child) { return child.remove(); });
            if (path.children.length === 1) {
                return reduceSingleChildCompoundPath(path);
            }
        }
    }
    return path;
}
function cleanupStrokeOutline(path, offset, strategy) {
    var result = normalize(path);
    if (strategy.simplifySelfIntersectingStroke && countSelfIntersections(result) > 0) {
        var simplified = result.clone({ insert: false });
        simplified.simplify(Math.max(Math.min(Math.abs(offset) * 0.02, 0.5), 0.1));
        result = normalize(simplified);
    }
    return result;
}
function isSameDirection(partialPath, fullPath) {
    var offset1 = partialPath.segments[0].location.offset;
    var offset2 = partialPath.segments[Math.max(1, Math.floor(partialPath.segments.length / 2))].location.offset;
    var sampleOffset = (offset1 + offset2) / 3;
    var originOffset1 = fullPath.getNearestLocation(partialPath.getPointAt(sampleOffset)).offset;
    var originOffset2 = fullPath.getNearestLocation(partialPath.getPointAt(2 * sampleOffset)).offset;
    return originOffset1 < originOffset2;
}
/** Remove self intersection when offset is negative by point direction dectection. */
function removeIntersection(path) {
    if (path.closed) {
        var newPath = path.unite(path, { insert: false });
        if (newPath instanceof paper.CompoundPath) {
            newPath.children.filter(function (c) {
                if (c.segments.length > 1) {
                    return !isSameDirection(c, path);
                }
                else {
                    return true;
                }
            }).forEach(function (c) { return c.remove(); });
            return reduceSingleChildCompoundPath(newPath);
        }
    }
    return path;
}
function getSegments(path) {
    if (path instanceof paper.CompoundPath) {
        return path.children.map(function (c) { return c.segments; }).flat();
    }
    else {
        return path.segments;
    }
}
/**
 * Remove impossible segments in negative offset condition.
 */
function removeOutsiders(newPath, path) {
    var segments = getSegments(newPath).slice();
    segments.forEach(function (segment) {
        if (!path.contains(segment.point)) {
            segment.remove();
        }
    });
}
function preparePath(path, offset) {
    var source = path.clone({ insert: false });
    source.reduce({});
    if (path.closed && !path.clockwise) {
        source.reverse();
        offset = -offset;
    }
    return [source, offset];
}
function getCircularPathRadius(path) {
    if (!path.closed || !path.applyMatrix || path.segments.length !== 4) {
        return null;
    }
    var bounds = path.bounds;
    var radius = (bounds.width + bounds.height) / 4;
    var scaleTolerance = Math.max(radius * 1e-6, 1e-7);
    if (radius <= scaleTolerance || Math.abs(bounds.width - bounds.height) > scaleTolerance) {
        return null;
    }
    var radialTolerance = Math.max(radius * 5e-4, 1e-6);
    var center = bounds.center;
    var sampleTimes = [0, 0.25, 0.5, 0.75, 1];
    var isCircular = path.curves.every(function (curve) {
        return sampleTimes.every(function (time) {
            var point = curve.getPointAtTime(time);
            return Math.abs(point.getDistance(center) - radius) <= radialTolerance;
        });
    });
    return isCircular ? radius : null;
}
function offsetCircularPath(path, offset) {
    var radius = getCircularPathRadius(path);
    if (radius === null) {
        return null;
    }
    var directedOffset = path.clockwise ? offset : -offset;
    var nextRadius = radius + directedOffset;
    var radiusTolerance = Math.max(radius * 1e-6, 1e-7);
    if (nextRadius <= radiusTolerance) {
        return makeEmptyOffsetResult(path);
    }
    var result = new paper.Path.Circle({
        center: path.bounds.center,
        radius: nextRadius,
        insert: false,
    });
    if (result.clockwise !== path.clockwise) {
        result.reverse();
    }
    return result;
}
function offsetSimpleShape(path, offset, join, limit, strategy) {
    var _a;
    var circularResult = offsetCircularPath(path, offset);
    if (circularResult) {
        return circularResult;
    }
    var source;
    _a = preparePath(path, offset), source = _a[0], offset = _a[1];
    var curves = source.curves.slice();
    var offsetCurves = curves.map(function (curve) { return adaptiveOffsetCurve(curve, offset, strategy); }).flat();
    var raws = [];
    for (var i = 0; i < offsetCurves.length; i += 2) {
        raws.push(offsetCurves.slice(i, i + 2));
    }
    var segments = connectBeziers(raws, join, source, offset, limit, strategy).flat();
    var newPath = removeIntersection(new paper.Path({ segments: segments, insert: false, closed: path.closed }));
    newPath.reduce({});
    if (source.closed && ((source.clockwise && offset < 0) || (!source.clockwise && offset > 0))) {
        removeOutsiders(newPath, path);
    }
    // recovery path
    if (source.clockwise !== path.clockwise) {
        newPath.reverse();
    }
    var result = normalize(newPath);
    if (source.closed && offset < 0 && hasContainmentLeak(source, result, offset)) {
        var clipped = result.intersect(source, { insert: false });
        result.remove();
        result = normalize(clipped);
    }
    return result;
}
function makeRoundCap(from, to, offset) {
    var origin = from.point.add(to.point).divide(2);
    var normal = to.point.subtract(from.point).rotate(-90, new paper.Point(0, 0)).normalize(offset);
    var through = origin.add(normal);
    var arc = new paper.Path.Arc({ from: from.point, to: to.point, through: through, insert: false });
    return arc.segments;
}
function connectSide(outer, inner, offset, cap) {
    if (outer instanceof paper.CompoundPath) {
        var cs = outer.children.map(function (c) { return ({ c: c, a: Math.abs(c.area) }); });
        cs = cs.sort(function (c1, c2) { return c2.a - c1.a; });
        outer = cs[0].c;
    }
    var oSegments = outer.segments.slice();
    var iSegments = inner.segments.slice();
    switch (cap) {
        case 'round':
            var heads = makeRoundCap(iSegments[iSegments.length - 1], oSegments[0], offset);
            var tails = makeRoundCap(oSegments[oSegments.length - 1], iSegments[0], offset);
            var result = new paper.Path({ segments: heads.concat(oSegments, tails, iSegments), closed: true, insert: false });
            result.reduce({});
            return result;
        default: return new paper.Path({ segments: oSegments.concat(iSegments), closed: true, insert: false });
    }
}
function offsetSimpleStroke(path, offset, join, cap, limit, strategy) {
    offset = path.closed && !path.clockwise ? -offset : offset;
    var positiveOffset = offsetSimpleShape(path, offset, join, limit, strategy);
    var negativeOffset = offsetSimpleShape(path, -offset, join, limit, strategy);
    if (path.closed) {
        return cleanupStrokeOutline(positiveOffset.subtract(negativeOffset, { insert: false }), offset, strategy);
    }
    else {
        var inner = negativeOffset;
        var holes = new Array();
        if (negativeOffset instanceof paper.CompoundPath) {
            holes = negativeOffset.children.filter(function (c) { return c.closed; });
            holes.forEach(function (h) { return h.remove(); });
            inner = negativeOffset.children[0];
        }
        inner.reverse();
        var final = connectSide(positiveOffset, inner, offset, cap);
        if (holes.length > 0) {
            for (var _i = 0, holes_1 = holes; _i < holes_1.length; _i++) {
                var hole = holes_1[_i];
                final = final.subtract(hole, { insert: false });
            }
        }
        return cleanupStrokeOutline(final, offset, strategy);
    }
}
function getChildPaths(path) {
    if (path instanceof paper.CompoundPath) {
        return path.children;
    }
    return [path];
}
function finiteNumber(value) {
    return Number.isFinite(value);
}
function countSelfIntersections(path) {
    if (!path.closed) {
        return 0;
    }
    try {
        var children_1 = getChildPaths(path);
        var count_1 = 0;
        children_1.forEach(function (child, index) {
            count_1 += child.getIntersections(child)
                .filter(function (location) { return location.isCrossing() || location.hasOverlap(); })
                .length;
            for (var i = index + 1; i < children_1.length; i += 1) {
                count_1 += child.getIntersections(children_1[i])
                    .filter(function (location) { return location.isCrossing() || location.hasOverlap(); })
                    .length;
            }
        });
        return count_1;
    }
    catch (error) {
        return 0;
    }
}
function needsAdditionalNormalization(path) {
    if (!(path instanceof paper.CompoundPath)) {
        return false;
    }
    var children = path.children;
    return children.some(function (child) { return !child.closed || child.segments.length === 0; })
        || countSelfIntersections(path) > 0;
}
function samplePoints(path, maxPerChild) {
    if (maxPerChild === void 0) { maxPerChild = 16; }
    var points = [];
    getChildPaths(path).forEach(function (child) {
        if (!child.length || child.segments.length === 0) {
            return;
        }
        var count = Math.min(maxPerChild, Math.max(4, Math.ceil(child.length / 30)));
        for (var i = 0; i < count; i += 1) {
            var ratio = child.closed ? i / count : i / Math.max(count - 1, 1);
            var point = child.getPointAt(child.length * ratio);
            if (point) {
                points.push(point);
            }
        }
    });
    return points;
}
function hasContainmentLeak(source, result, offset) {
    var tolerance = Math.max(Math.abs(offset) * 0.08, 0.5);
    return samplePoints(result).some(function (point) {
        return !containsPoint(source, point) && distanceToPath(source, point) > tolerance;
    });
}
function containsPoint(path, point) {
    try {
        return path.contains(point);
    }
    catch (error) {
        return false;
    }
}
function distanceToPath(path, point) {
    try {
        var nearest = path.getNearestLocation(point);
        return nearest ? nearest.distance : Number.POSITIVE_INFINITY;
    }
    catch (error) {
        return Number.POSITIVE_INFINITY;
    }
}
function isExpectedJoinOrCapShortcut(location, options) {
    var timeTolerance = 1e-7;
    var atCurveEndpoint = location.time <= timeTolerance || location.time >= 1 - timeTolerance;
    if ((options.join || 'miter') !== 'round' && atCurveEndpoint) {
        return true;
    }
    if (options.stroke && (options.cap || 'butt') !== 'round' && !location.path.closed) {
        var offsetTolerance = Math.max(location.path.length * 1e-8, 1e-7);
        return location.offset <= offsetTolerance || location.path.length - location.offset <= offsetTolerance;
    }
    return false;
}
function addWarning(warnings, warning) {
    if (warnings.indexOf(warning) === -1) {
        warnings.push(warning);
    }
}
export function analyzeOffsetQuality(source, result, offset, options) {
    if (options === void 0) { options = {}; }
    var warnings = new Array();
    var expectedClosed = options.stroke ? true : source.closed;
    var segmentCount = getSegments(result).length;
    var area = Math.abs(result.area || 0);
    var absOffset = Math.abs(offset);
    var allowsEmptyResult = source.closed && offset < 0 && !options.stroke;
    var score = segmentCount * 0.01;
    var containmentErrors = 0;
    var distanceErrors = 0;
    var bounds = result.bounds;
    if (segmentCount === 0) {
        score += allowsEmptyResult ? 500 : 100000;
        addWarning(warnings, 'empty-result');
    }
    if (!bounds || !finiteNumber(bounds.x) || !finiteNumber(bounds.y) || !finiteNumber(bounds.width) || !finiteNumber(bounds.height)) {
        score += 100000;
        addWarning(warnings, 'non-finite-bounds');
    }
    if (result.closed !== expectedClosed && !(allowsEmptyResult && segmentCount === 0)) {
        score += 50000;
        addWarning(warnings, 'unexpected-closed-state');
    }
    var selfIntersections = countSelfIntersections(result);
    if (selfIntersections > 0) {
        score += selfIntersections * 10000;
        addWarning(warnings, 'self-intersection');
    }
    if (source.closed && result.closed && !options.stroke) {
        var sourceArea = Math.abs(source.area || 0);
        if (sourceArea > 0 && offset > 0 && area < sourceArea * 0.95) {
            score += ((sourceArea - area) / sourceArea) * 2000;
            addWarning(warnings, 'positive-offset-area-shrank');
        }
        if (sourceArea > 0 && offset < 0 && area > sourceArea * 1.05) {
            score += ((area - sourceArea) / sourceArea) * 2000;
            addWarning(warnings, 'negative-offset-area-grew');
        }
        if (sourceArea > 0 && offset > 0) {
            var sourceBounds = source.bounds;
            var centerShift = result.bounds.center.getDistance(sourceBounds.center);
            var centerTolerance = Math.max(absOffset * 1.15, Math.min(sourceBounds.width, sourceBounds.height) * 0.08);
            if (centerShift > centerTolerance) {
                score += (centerShift - centerTolerance) * 80;
                addWarning(warnings, 'offset-center-shift');
            }
        }
        var containmentTolerance_1 = Math.max(absOffset * 0.08, 0.5);
        var containmentSamples = offset >= 0 ? samplePoints(source) : samplePoints(result);
        containmentSamples.forEach(function (point) {
            var container = offset >= 0 ? result : source;
            if (!containsPoint(container, point) && distanceToPath(container, point) > containmentTolerance_1) {
                containmentErrors += 1;
            }
        });
        if (containmentErrors > 0) {
            score += containmentErrors * 150;
            addWarning(warnings, offset >= 0 ? 'source-outside-positive-offset' : 'negative-offset-outside-source');
        }
    }
    if (absOffset > 0.001) {
        var distanceTolerance_1 = Math.max(absOffset * 0.7, 0.75);
        samplePoints(result).forEach(function (point) {
            var nearest = null;
            try {
                nearest = source.getNearestLocation(point);
            }
            catch (error) {
                nearest = null;
            }
            if (nearest && finiteNumber(nearest.distance) && nearest.distance < distanceTolerance_1
                && !isExpectedJoinOrCapShortcut(nearest, options)) {
                distanceErrors += 1;
            }
        });
        if (distanceErrors > 0) {
            score += distanceErrors * 200;
            addWarning(warnings, 'offset-distance-collapse');
        }
    }
    return {
        score: score,
        warnings: warnings,
        selfIntersections: selfIntersections,
        containmentErrors: containmentErrors,
        distanceErrors: distanceErrors,
        segmentCount: segmentCount,
        area: area,
    };
}
function needsAutoFallback(quality, includeDistanceCollapse) {
    if (includeDistanceCollapse === void 0) { includeDistanceCollapse = false; }
    return quality.warnings.some(function (warning) {
        return includeDistanceCollapse || warning !== 'offset-distance-collapse';
    });
}
function isExpectedEmptyInwardResult(source, offset, quality) {
    return source.closed
        && offset < 0
        && quality.segmentCount === 0
        && !hasStrictInteriorClearance(source, offset);
}
function pickBestResult(source, offset, makeResult) {
    var bestResult = null;
    var bestQuality = null;
    var firstError = null;
    for (var _i = 0, AUTO_STRATEGIES_1 = AUTO_STRATEGIES; _i < AUTO_STRATEGIES_1.length; _i++) {
        var strategy = AUTO_STRATEGIES_1[_i];
        try {
            var candidate = makeResult(strategy);
            var quality = candidate.quality
                || analyzeOffsetQuality(source, candidate.result, offset, candidate.qualityOptions);
            quality.algorithm = strategy.algorithm;
            if (!bestQuality || quality.score < bestQuality.score) {
                bestResult = candidate.result;
                bestQuality = quality;
            }
            if (strategy.algorithm === 'adaptive'
                && (!needsAutoFallback(quality, offset < 0 && !candidate.qualityOptions.stroke) || isExpectedEmptyInwardResult(source, offset, quality))) {
                return candidate.result;
            }
        }
        catch (error) {
            if (!firstError && error instanceof Error) {
                firstError = error;
            }
        }
    }
    if (!bestResult) {
        throw firstError || new Error('Unable to offset path with any algorithm');
    }
    return bestResult;
}
function getNonSelfIntersectionPath(path) {
    if (path.closed && (!path.clockwise || countSelfIntersections(path) > 0)) {
        return path.unite(path, { insert: false });
    }
    return path;
}
function offsetCompoundChild(child, parent, offset, join, limit, strategy) {
    if (child.segments.length <= 1) {
        return null;
    }
    if (!isSameDirection(child, parent)) {
        child.reverse();
    }
    var childOffset = offsetSimpleShape(child, offset, join, limit, strategy);
    if (needsAdditionalNormalization(childOffset)) {
        childOffset = normalize(childOffset);
    }
    if (childOffset.clockwise !== child.clockwise) {
        childOffset.reverse();
    }
    if (childOffset instanceof paper.CompoundPath) {
        childOffset.applyMatrix = true;
        return childOffset.children;
    }
    return childOffset;
}
function groupCompoundContours(children) {
    var groups = children
        .filter(function (child) { return child.clockwise; })
        .map(function (outer) { return ({ outer: outer, holes: [] }); });
    var standalone = new Array();
    children.filter(function (child) { return !child.clockwise; }).forEach(function (hole) {
        var holeArea = Math.abs(hole.area);
        var parentIndex = -1;
        var parentArea = Number.POSITIVE_INFINITY;
        groups.forEach(function (group, index) {
            var outerArea = Math.abs(group.outer.area);
            if (outerArea > holeArea && outerArea < parentArea && group.outer.contains(hole.interiorPoint)) {
                parentIndex = index;
                parentArea = outerArea;
            }
        });
        if (parentIndex >= 0) {
            groups[parentIndex].holes.push(hole);
        }
        else {
            standalone.push(hole);
        }
    });
    return { groups: groups, standalone: standalone };
}
function asCompoundOffsetPath(value) {
    return value instanceof Array
        ? new paper.CompoundPath({ children: value, insert: false })
        : value;
}
function offsetCompoundShape(path, offset, join, limit, strategy) {
    var children = path.children.filter(function (child) { return child.segments.length > 1; });
    var outers = children.filter(function (child) { return child.clockwise; });
    var holes = children.filter(function (child) { return !child.clockwise; });
    if (outers.length === 0 || holes.length === 0) {
        var offsetParts = children.map(function (child) { return offsetCompoundChild(child, path, offset, join, limit, strategy); });
        return new paper.CompoundPath({ children: offsetParts.flat().filter(function (child) { return !!child; }), insert: false });
    }
    var contours = groupCompoundContours(children);
    var pieces = contours.groups.map(function (group) {
        var outerOffset = offsetCompoundChild(group.outer, path, offset, join, limit, strategy);
        var result = asCompoundOffsetPath(outerOffset);
        group.holes.forEach(function (hole) {
            var holeOffset = offsetCompoundChild(hole, path, offset, join, limit, strategy);
            if (!holeOffset) {
                return;
            }
            var holePath = asCompoundOffsetPath(holeOffset);
            var next = result.subtract(holePath, { insert: false });
            result.remove();
            holePath.remove();
            result = next;
        });
        return result;
    });
    contours.standalone.forEach(function (child) {
        var childOffset = offsetCompoundChild(child, path, offset, join, limit, strategy);
        if (childOffset) {
            pieces.push(asCompoundOffsetPath(childOffset));
        }
    });
    if (pieces.length === 1) {
        return pieces[0];
    }
    return pieces.slice(1).reduce(function (result, piece) {
        var next = result.unite(piece, { insert: false });
        result.remove();
        piece.remove();
        return next;
    }, pieces[0]);
}
function hasCompoundHoles(path) {
    if (!(path instanceof paper.CompoundPath)) {
        return false;
    }
    var children = path.children;
    return children.some(function (child) { return child.clockwise; }) && children.some(function (child) { return !child.clockwise; });
}
function withoutAdaptiveFallback(strategy) {
    return {
        algorithm: 'robust',
        splitSelfIntersectingCurves: strategy.splitSelfIntersectingCurves,
        guardInsideJoins: strategy.guardInsideJoins,
        simplifySelfIntersectingStroke: strategy.simplifySelfIntersectingStroke,
        adaptiveFallback: false,
    };
}
function addAdaptiveCandidate(candidates, qualityOptions, makeResult) {
    try {
        candidates.push({ result: makeResult(), qualityOptions: qualityOptions });
    }
    catch (error) {
        // Keep adaptive mode opportunistic: a failed fallback should not hide valid strict results.
    }
}
function pickBestCandidate(source, offset, candidates) {
    var bestCandidate = candidates[0];
    if (candidates.length === 1) {
        return bestCandidate;
    }
    var bestQuality = bestCandidate.quality
        || analyzeOffsetQuality(source, bestCandidate.result, offset, bestCandidate.qualityOptions);
    for (var i = 1; i < candidates.length; i += 1) {
        var quality = candidates[i].quality
            || analyzeOffsetQuality(source, candidates[i].result, offset, candidates[i].qualityOptions);
        if (quality.score < bestQuality.score) {
            bestCandidate = candidates[i];
            bestQuality = quality;
        }
    }
    bestCandidate.quality = bestQuality;
    return bestCandidate;
}
function isSeverelyCollapsedInwardResult(source, result, offset) {
    if (!source.closed || offset >= 0) {
        return false;
    }
    var distances = samplePoints(result)
        .map(function (point) { return distanceToPath(source, point); })
        .filter(finiteNumber)
        .sort(function (a, b) { return a - b; });
    if (distances.length === 0) {
        return false;
    }
    var collapsedCount = distances.filter(function (distance) { return distance < Math.abs(offset) * 0.7; }).length;
    if (collapsedCount * 2 < distances.length) {
        return false;
    }
    return !hasInteriorClearance(source, offset);
}
function hasInteriorClearance(source, offset) {
    return findInteriorPointWithClearance(source, Math.abs(offset) * INTERIOR_CLEARANCE_FACTOR) !== null;
}
function hasStrictInteriorClearance(source, offset) {
    var distance = Math.abs(offset);
    var tolerance = Math.max(distance * 1e-6, 1e-7);
    return findInteriorPointWithClearance(source, distance + tolerance) !== null;
}
function findInteriorPointWithClearance(source, requiredClearance) {
    var candidates = getChildPaths(source).map(function (child) { return child.interiorPoint; });
    var bounds = source.bounds;
    candidates.push(bounds.center);
    var divisions = 11;
    for (var y = 0; y < divisions; y += 1) {
        for (var x = 0; x < divisions; x += 1) {
            candidates.push(new paper.Point(bounds.left + bounds.width * (x + 0.5) / divisions, bounds.top + bounds.height * (y + 0.5) / divisions));
        }
    }
    var bestPoint = null;
    var bestClearance = 0;
    var checkPoint = function (point) {
        if (!containsPoint(source, point)) {
            return false;
        }
        var clearance = distanceToPath(source, point);
        if (clearance > bestClearance) {
            bestPoint = point;
            bestClearance = clearance;
        }
        return clearance >= requiredClearance ? point : null;
    };
    for (var _i = 0, candidates_1 = candidates; _i < candidates_1.length; _i++) {
        var candidate = candidates_1[_i];
        var point = checkPoint(candidate);
        if (point) {
            return point;
        }
    }
    var stepX = bounds.width / divisions;
    var stepY = bounds.height / divisions;
    for (var iteration = 0; iteration < 6 && bestPoint; iteration += 1) {
        var center = bestPoint;
        var nearby = new Array();
        for (var y = -1; y <= 1; y += 1) {
            for (var x = -1; x <= 1; x += 1) {
                nearby.push(center.add(new paper.Point(stepX * x, stepY * y)));
            }
        }
        for (var _a = 0, nearby_1 = nearby; _a < nearby_1.length; _a++) {
            var candidate = nearby_1[_a];
            var point = checkPoint(candidate);
            if (point) {
                return point;
            }
        }
        stepX /= 2;
        stepY /= 2;
    }
    return null;
}
function makeEmptyOffsetResult(source) {
    var result = new paper.Path({ insert: false });
    result.copyAttributes(source, false);
    return result;
}
function makeBoundaryBuffer(source, radius) {
    var parts = new Array();
    var flatness = Math.max(0.01, Math.min(radius * 0.02, 0.5));
    getChildPaths(source).forEach(function (child) {
        var flattened = child.clone({ insert: false });
        flattened.flatten(flatness);
        flattened.segments.forEach(function (segment) {
            var next = segment.next;
            if (!next) {
                return;
            }
            var delta = next.point.subtract(segment.point);
            if (delta.length <= 1e-8) {
                return;
            }
            var direction = delta.normalize(radius);
            var normal = direction.rotate(90, new paper.Point(0, 0));
            var capsule = new paper.Path({
                segments: [segment.point.add(normal)],
                insert: false,
            });
            capsule.lineTo(next.point.add(normal));
            capsule.arcTo(next.point.add(direction), next.point.subtract(normal));
            capsule.lineTo(segment.point.subtract(normal));
            capsule.arcTo(segment.point.subtract(direction), segment.point.add(normal));
            capsule.closed = true;
            parts.push(capsule);
        });
        flattened.remove();
    });
    if (parts.length === 0) {
        return null;
    }
    // Balanced unions keep intermediate contour complexity bounded on flattened Bezier paths.
    var level = parts;
    while (level.length > 1) {
        var nextLevel = new Array();
        for (var i = 0; i < level.length; i += 2) {
            if (i + 1 === level.length) {
                nextLevel.push(level[i]);
                continue;
            }
            var united = level[i].unite(level[i + 1], { insert: false });
            level[i].remove();
            level[i + 1].remove();
            nextLevel.push(united);
        }
        level = nextLevel;
    }
    return level[0];
}
function recoverRoundInwardOffsetFromBoundary(source, offset) {
    var buffer = makeBoundaryBuffer(source, Math.abs(offset));
    if (!buffer) {
        return makeEmptyOffsetResult(source);
    }
    var recovered = normalize(source.subtract(buffer, { insert: false }));
    buffer.remove();
    return recovered;
}
function isMissingInwardInterior(source, result, offset) {
    var requiredClearance = Math.abs(offset) * 1.02;
    var missesInteriorPoint = getChildPaths(source).some(function (child) {
        var point = child.interiorPoint;
        return containsPoint(source, point)
            && distanceToPath(source, point) >= requiredClearance
            && !containsPoint(result, point);
    });
    if (missesInteriorPoint) {
        return true;
    }
    var sourceArea = Math.abs(source.area || 0);
    var resultArea = Math.abs(result.area || 0);
    if (sourceArea === 0 || resultArea >= sourceArea * 0.05) {
        return false;
    }
    var clearancePoint = findInteriorPointWithClearance(source, requiredClearance);
    return clearancePoint !== null && !containsPoint(result, clearancePoint);
}
function needsRoundInwardRecovery(source, result, offset) {
    var quality = analyzeOffsetQuality(source, result, offset, { join: 'round' });
    if (quality.selfIntersections > 0 || quality.containmentErrors > 0 || quality.distanceErrors > 0) {
        return true;
    }
    if (quality.segmentCount === 0) {
        return hasStrictInteriorClearance(source, offset);
    }
    if (!hasStrictInteriorClearance(source, offset)) {
        return true;
    }
    return isMissingInwardInterior(source, result, offset);
}
function recoverInwardOffsetFromComplement(source, offset, join, limit, strategy) {
    var padding = Math.abs(offset) * 4 + Math.max(source.bounds.width, source.bounds.height);
    var frame = new paper.Path.Rectangle({
        rectangle: source.bounds.expand(padding),
        insert: false,
    });
    var background = frame.subtract(source, { insert: false });
    var expandedBackground = offsetPathWithStrategy(background, Math.abs(offset), join, limit, strategy);
    var recovered = normalize(frame.subtract(expandedBackground, { insert: false }));
    background.remove();
    expandedBackground.remove();
    frame.remove();
    return recovered;
}
function isNearInwardCollapse(source, result, offset) {
    if (!source.closed || offset >= 0 || getSegments(result).length === 0) {
        return false;
    }
    var sourceArea = Math.abs(source.area || 0);
    return sourceArea > 0 && Math.abs(result.area || 0) <= sourceArea * 0.4;
}
function shouldPreferRoundInwardCandidate(source, primary, rounded, join) {
    var sourceArea = Math.abs(source.area || 0);
    var primaryArea = Math.abs(primary.area || 0);
    var roundedArea = Math.abs(rounded.area || 0);
    var areaTolerance = Math.max(sourceArea * 1e-5, 0.5);
    if (getSegments(rounded).length === 0) {
        return getSegments(primary).length > 0;
    }
    if (roundedArea <= areaTolerance) {
        return primaryArea > areaTolerance;
    }
    var areaRatio = primaryArea / roundedArea;
    var maximumStableRatio = join === 'miter' ? 1.25 : 4;
    return areaRatio < 0.9 || areaRatio > maximumStableRatio;
}
function offsetPathAdaptive(path, offset, join, limit, strategy) {
    var _a;
    var baseStrategy = withoutAdaptiveFallback(strategy);
    var candidates = new Array();
    var compareInwardStyles = false;
    addAdaptiveCandidate(candidates, { join: join }, function () { return offsetPathWithStrategy(path, offset, join, limit, baseStrategy); });
    if (join !== 'round' && candidates.length > 0) {
        var primary = candidates[0];
        primary.quality = analyzeOffsetQuality(path, primary.result, offset, primary.qualityOptions);
        compareInwardStyles = isNearInwardCollapse(path, primary.result, offset);
        if ((!needsAutoFallback(primary.quality, offset < 0)
            || isExpectedEmptyInwardResult(path, offset, primary.quality))
            && !isSeverelyCollapsedInwardResult(path, primary.result, offset)
            && !compareInwardStyles) {
            return primary;
        }
    }
    var roundCandidateIndex = candidates.length;
    if (join !== 'round') {
        addAdaptiveCandidate(candidates, { join: 'round' }, function () { return offsetPathWithStrategy(path, offset, 'round', limit, baseStrategy); });
    }
    if (candidates.length === 0) {
        return {
            result: offsetPathWithStrategy(path, offset, join, limit, baseStrategy),
            qualityOptions: { join: join },
        };
    }
    var hasRoundCandidate = candidates.length > roundCandidateIndex;
    var preferRoundCandidate = compareInwardStyles && hasRoundCandidate
        && shouldPreferRoundInwardCandidate(path, candidates[0].result, candidates[roundCandidateIndex].result, join);
    var primaryNeedsFallback = Boolean(((_a = candidates[0]) === null || _a === void 0 ? void 0 : _a.quality)
        && needsAutoFallback(candidates[0].quality, offset < 0));
    var candidate = compareInwardStyles && hasRoundCandidate
        ? preferRoundCandidate
            ? candidates[roundCandidateIndex]
            : primaryNeedsFallback
                ? pickBestCandidate(path, offset, candidates)
                : candidates[0]
        : pickBestCandidate(path, offset, candidates);
    if (isSeverelyCollapsedInwardResult(path, candidate.result, offset)) {
        return {
            result: makeEmptyOffsetResult(path),
            qualityOptions: candidate.qualityOptions,
        };
    }
    return candidate;
}
function offsetPathWithStrategy(path, offset, join, limit, strategy) {
    if (strategy.adaptiveFallback) {
        return offsetPathAdaptive(path, offset, join, limit, strategy).result;
    }
    var nonSIPath = getNonSelfIntersectionPath(path);
    var shouldNormalizeResult = !hasCompoundHoles(nonSIPath);
    var result = nonSIPath;
    if (nonSIPath instanceof paper.Path) {
        result = offsetSimpleShape(nonSIPath, offset, join, limit, strategy);
    }
    else {
        result = offsetCompoundShape(nonSIPath, offset, join, limit, strategy);
    }
    if (shouldNormalizeResult && needsAdditionalNormalization(result)) {
        result = normalize(result);
    }
    if (strategy.guardInsideJoins
        && join === 'round'
        && offset < 0
        && path.closed
        && needsRoundInwardRecovery(nonSIPath, result, offset)) {
        var recovered = recoverRoundInwardOffsetFromBoundary(nonSIPath, offset);
        result.remove();
        result = recovered;
        if (isSeverelyCollapsedInwardResult(nonSIPath, result, offset)) {
            result.remove();
            result = makeEmptyOffsetResult(nonSIPath);
        }
    }
    var shouldRecoverInwardOffset = offset < 0
        && path.closed
        && getSegments(result).length === 0
        && hasStrictInteriorClearance(nonSIPath, offset);
    if (shouldRecoverInwardOffset) {
        result.remove();
        result = recoverInwardOffsetFromComplement(nonSIPath, offset, join, limit, strategy);
    }
    result.copyAttributes(nonSIPath, false);
    result.remove();
    return result;
}
export function offsetPath(path, offset, join, limit, algorithm) {
    if (algorithm === void 0) { algorithm = 'auto'; }
    if (algorithm === 'auto') {
        return pickBestResult(path, offset, function (strategy) {
            if (strategy.adaptiveFallback) {
                return offsetPathAdaptive(path, offset, join, limit, strategy);
            }
            return {
                result: offsetPathWithStrategy(path, offset, join, limit, strategy),
                qualityOptions: { join: join },
            };
        });
    }
    return offsetPathWithStrategy(path, offset, join, limit, getStrategy(algorithm));
}
function offsetStrokeWithStrategy(path, offset, join, cap, limit, strategy) {
    if (strategy.adaptiveFallback) {
        return offsetStrokeAdaptive(path, offset, join, cap, limit, strategy).result;
    }
    var nonSIPath = getNonSelfIntersectionPath(path);
    var result = nonSIPath;
    if (nonSIPath instanceof paper.Path) {
        result = offsetSimpleStroke(nonSIPath, offset, join, cap, limit, strategy);
    }
    else {
        var children = nonSIPath.children.flatMap(function (c) {
            return offsetSimpleStroke(c, offset, join, cap, limit, strategy);
        });
        result = children.reduce(function (c1, c2) { return c1.unite(c2, { insert: false }); });
    }
    result.strokeWidth = 0;
    result.fillColor = nonSIPath.strokeColor;
    result.shadowBlur = nonSIPath.shadowBlur;
    result.shadowColor = nonSIPath.shadowColor;
    result.shadowOffset = nonSIPath.shadowOffset;
    return result;
}
function offsetStrokeAdaptive(path, offset, join, cap, limit, strategy) {
    var baseStrategy = withoutAdaptiveFallback(strategy);
    var candidates = new Array();
    addAdaptiveCandidate(candidates, { stroke: true, join: join, cap: cap }, function () { return offsetStrokeWithStrategy(path, offset, join, cap, limit, baseStrategy); });
    if ((join !== 'round' || cap !== 'round') && candidates.length > 0) {
        var primary = candidates[0];
        primary.quality = analyzeOffsetQuality(path, primary.result, offset, primary.qualityOptions);
        if (!needsAutoFallback(primary.quality)) {
            return primary;
        }
    }
    if (join !== 'round') {
        addAdaptiveCandidate(candidates, { stroke: true, join: 'round', cap: cap }, function () { return offsetStrokeWithStrategy(path, offset, 'round', cap, limit, baseStrategy); });
    }
    if (cap !== 'round') {
        addAdaptiveCandidate(candidates, { stroke: true, join: join, cap: 'round' }, function () { return offsetStrokeWithStrategy(path, offset, join, 'round', limit, baseStrategy); });
    }
    if (join !== 'round' && cap !== 'round') {
        addAdaptiveCandidate(candidates, { stroke: true, join: 'round', cap: 'round' }, function () { return offsetStrokeWithStrategy(path, offset, 'round', 'round', limit, baseStrategy); });
    }
    if (candidates.length === 0) {
        return {
            result: offsetStrokeWithStrategy(path, offset, join, cap, limit, baseStrategy),
            qualityOptions: { stroke: true, join: join, cap: cap },
        };
    }
    return pickBestCandidate(path, offset, candidates);
}
export function offsetStroke(path, offset, join, cap, limit, algorithm) {
    if (algorithm === void 0) { algorithm = 'auto'; }
    if (algorithm === 'auto') {
        return pickBestResult(path, offset, function (strategy) {
            if (strategy.adaptiveFallback) {
                return offsetStrokeAdaptive(path, offset, join, cap, limit, strategy);
            }
            return {
                result: offsetStrokeWithStrategy(path, offset, join, cap, limit, strategy),
                qualityOptions: { stroke: true, join: join, cap: cap },
            };
        });
    }
    return offsetStrokeWithStrategy(path, offset, join, cap, limit, getStrategy(algorithm));
}
//# sourceMappingURL=offset_core.js.map